import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { KeyRound, Pencil, Plus, Users } from 'lucide-react';
import { apiGet, apiPut, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useToast } from '../../hooks/useToast.js';
import { CreateUserDialog } from './CreateUserDialog.js';
import type { CreateUserResult } from './CreateUserDialog.js';
import { ResetPasswordDialog } from './ResetPasswordDialog.js';

export interface UserRow {
  id: string;
  username: string;
  height_cm: number | null;
  must_reset_password: 0 | 1;
  created_at: string;
}

export function UserManagement(): ReactElement {
  const { status } = useAuth();
  const { push } = useToast();
  const currentUsername = useMemo(() => {
    return typeof status !== 'string' && status.authenticated ? status.username : undefined;
  }, [status]);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await apiGet<UserRow[]>('/api/users');
      setUsers(list);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load users';
      push('error', msg);
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function startEdit(row: UserRow): void {
    setEditingId(row.id);
    setEditValue(row.height_cm !== null ? String(row.height_cm) : '');
  }

  function cancelEdit(): void {
    setEditingId(null);
    setEditValue('');
  }

  async function saveEdit(row: UserRow): Promise<void> {
    const n = Number(editValue);
    if (!Number.isInteger(n) || n < 50 || n > 250) {
      push('error', 'Height must be between 50 and 250 cm');
      return;
    }
    setSavingId(row.id);
    try {
      await apiPut('/api/users/profile', { height_cm: n });
      setUsers((prev) =>
        prev.map((u) => (u.id === row.id ? { ...u, height_cm: n } : u)),
      );
      push('success', 'Height updated');
      cancelEdit();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to update height';
      push('error', msg);
    } finally {
      setSavingId(null);
    }
  }

  function onCreated(created: CreateUserResult): void {
    const row: UserRow = {
      id: created.id,
      username: created.username,
      height_cm: created.height_cm,
      must_reset_password: created.must_reset_password,
      created_at: new Date().toISOString(),
    };
    setUsers((prev) => [...prev, row]);
  }

  return (
    <section
      aria-label="User Management"
      data-testid="user-management-section"
      className="rounded-2xl border p-5 shadow-sm"
      style={{
        background: 'var(--bg-surface)',
        borderColor: 'var(--border)',
        color: 'var(--text-main)',
      }}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} aria-hidden="true" style={{ color: 'var(--accent)' }} />
          <h2 className="text-lg font-semibold">User Management</h2>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium"
          data-testid="create-user-button"
          style={{
            background: 'var(--accent)',
            color: 'var(--accent-contrast)',
            minHeight: 48,
          }}
        >
          <Plus size={16} aria-hidden="true" />
          <span>Create New User</span>
        </button>
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }} data-testid="users-loading">
          Loading users…
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }} data-testid="users-empty">
          No users yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="user-list">
          {users.map((row) => {
            const isMe = row.username === currentUsername;
            const isEditing = editingId === row.id;
            const isSaving = savingId === row.id;
            return (
              <li
                key={row.id}
                data-testid={`user-row-${row.username}`}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span>{row.username}</span>
                    {isMe ? (
                      <span
                        className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                        style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
                        data-testid={`you-badge-${row.username}`}
                      >
                        You
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {isEditing ? (
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          inputMode="numeric"
                          min={50}
                          max={250}
                          step={1}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 rounded border px-1 py-0.5 text-xs"
                          style={{
                            background: 'var(--bg-primary)',
                            borderColor: 'var(--border)',
                            color: 'var(--text-main)',
                          }}
                          data-testid={`edit-input-${row.username}`}
                        />
                        <span>cm</span>
                      </span>
                    ) : (
                      <span data-testid={`height-${row.username}`}>
                        Height: {row.height_cm !== null ? `${row.height_cm} cm` : '—'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isMe ? (
                    isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void saveEdit(row)}
                          disabled={isSaving}
                          className="rounded-md px-2 py-1 text-xs font-medium"
                          data-testid={`save-edit-${row.username}`}
                          style={{
                            background: 'var(--accent)',
                            color: 'var(--accent-contrast)',
                            minHeight: 48,
                            minWidth: 48,
                          }}
                        >
                          {isSaving ? '…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="rounded-md border px-2 py-1 text-xs"
                          data-testid={`cancel-edit-${row.username}`}
                          style={{
                            background: 'transparent',
                            borderColor: 'var(--border)',
                            color: 'var(--text-main)',
                            minHeight: 48,
                            minWidth: 48,
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Edit height for ${row.username}`}
                        onClick={() => startEdit(row)}
                        className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md"
                        data-testid={`edit-button-${row.username}`}
                        style={{ color: 'var(--text-main)' }}
                      >
                        <Pencil size={16} aria-hidden="true" />
                      </button>
                    )
                  ) : null}
                  {!isMe ? (
                    <button
                      type="button"
                      aria-label={`Reset password for ${row.username}`}
                      onClick={() => setResetTarget(row)}
                      className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md"
                      data-testid={`reset-pwd-button-${row.username}`}
                      style={{ color: 'var(--text-main)' }}
                    >
                      <KeyRound size={16} aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreated}
      />
      {resetTarget ? (
        <ResetPasswordDialog
          open
          userId={resetTarget.id}
          username={resetTarget.username}
          onClose={() => setResetTarget(null)}
        />
      ) : null}
    </section>
  );
}